import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../i18n/index.js';

const LanguageContext = createContext({});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { t: i18nT, i18n } = useTranslation();

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('sj_language') || 'en';
  });

  // Keep i18next in sync with stored preference on mount
  useEffect(() => {
    const stored = localStorage.getItem('sj_language') || 'en';
    if (i18n?.language !== stored) {
      i18n?.changeLanguage(stored);
    }
  }, [i18n]);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('sj_language', lang);
    i18n?.changeLanguage(lang);
  }, [i18n]);

  /**
   * t(key, vars) — drop-in replacement for the existing custom t() function.
   * Supports {placeholder} style interpolation used throughout the app.
   */
  const t = useCallback((key, vars) => {
    // i18next is configured with prefix='{' suffix='}' so it handles {var} natively.
    // Pass vars directly; fall back to key string if translation is missing.
    const result = i18nT(key, vars || {});
    // i18next returns the key when missing — that's the same fallback as before.
    return result;
  }, [i18nT]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
